/**
 * DD Buy Box — Web Component
 *
 * Cart quantity logic:
 *   cartQty = Math.ceil(selectedPackSize / basePackQty)
 *   Example: 6-Pack ÷ basePackQty(2) = 3 units added to cart
 *
 * For Variety Pack, each flavor's cart quantity:
 *   Math.ceil(flavorMixQty / basePackQty)
 */
class DDBuyBox extends HTMLElement {
  connectedCallback() {
    // The mobile layout IIFE moves this element in the DOM, which re-fires
    // connectedCallback and would double-bind every event listener.
    if (this._initialized) return;
    this._initialized = true;

    // Internal state — never read DOM to derive state, always read these
    this._selectedVariantId = 'UNSET'; // string variant ID, null = variety, 'UNSET' = nothing chosen
    this._selectedPackSize  = null;    // integer: 4 | 6 | 12
    this._purchaseType      = 'one-time'; // 'one-time' | 'subscription'
    this._selectedPlanId    = null;    // string selling plan ID or null
    this._mixQtys           = {};      // { variantId: integer }
    this._basePackQty       = parseInt(this.dataset.basePackQty, 10) || 1;
    this._moneyFmt          = null;
    this._shopCurrency      = null;
    this._atcLabel          = this.querySelector('.dbb-atc-btn')?.textContent.trim() || 'Add to Cart';

    this._bindFlavors();
    this._bindSizes();
    this._bindPurchase();
    this._bindMix();
    this._bindAtc();
    this._bindCurrencyChange();
    this._initDefaults();
  }

  // ── Defaults ──────────────────────────────────────────────────────────────
  // Run after all listeners are bound so every _select* call gets a full update.

  _initDefaults() {
    // 1. Size — honour is-selected set by Liquid (default_selected block), else first
    const preSelected = this.querySelector('.dbb-size-item.is-selected') || this.querySelector('.dbb-size-item');
    if (preSelected) this._selectSizeItem(preSelected);

    // 2. Flavor — picks first non-variety variant and enables ATC
    const firstFlavor = this.querySelector('.dbb-flavor-item');
    if (firstFlavor) this._selectFlavorItem(firstFlavor);

    // 3. Purchase — prefer subscription card, fall back to one-time
    const subCard     = this.querySelector('.dbb-purchase-card[data-purchase-type="subscription"]');
    const onetimeCard = this.querySelector('.dbb-purchase-card[data-purchase-type="one-time"]');
    if (subCard) {
      this._selectPurchaseCard(subCard);
    } else if (onetimeCard) {
      this._selectPurchaseCard(onetimeCard);
    } else {
      this._syncPlanFromDom();
    }
  }

  // Reads the currently-selected frequency select and syncs _selectedPlanId.
  _syncPlanFromDom() {
    const sel = this.querySelector('.dbb-frequency-select');
    if (sel && sel.value) {
      this._selectedPlanId = sel.value;
    }
  }

  // ── Flavor ────────────────────────────────────────────────────────────────

  _bindFlavors() {
    const items = [...this.querySelectorAll('.dbb-flavor-item')];
    items.forEach((btn, idx) => {
      btn.addEventListener('click', () => this._selectFlavorItem(btn));
      btn.addEventListener('keydown', (e) => {
        let next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); next = (idx + 1) % items.length; }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); next = (idx - 1 + items.length) % items.length; }
        if (next >= 0) { items[next].focus(); this._selectFlavorItem(items[next]); }
      });
    });
  }

  _selectFlavorItem(btn) {
    this.querySelectorAll('.dbb-flavor-item').forEach(b => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
      b.tabIndex = -1;
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');
    btn.tabIndex = 0;

    const isVariety = btn.dataset.flavor === 'variety';
    this._selectedVariantId = isVariety ? null : (btn.dataset.variantId || null);

    const label = this.querySelector('[data-flavor-label]');
    if (label) {
      const nameEl = btn.querySelector('.dbb-flavor-name');
      label.textContent = nameEl ? nameEl.textContent.trim() : '';
    }

    const builder = this.querySelector('.dbb-mix-builder');
    if (builder) {
      builder.classList.toggle('is-visible', isVariety);
      if (isVariety) {
        if (this._selectedPackSize) {
          this._autoDistributeMix();
        } else {
          this._resetMix();
        }
      }
    }

    this._updateAtcState();
  }

  // ── Size ──────────────────────────────────────────────────────────────────

  _bindSizes() {
    const items = [...this.querySelectorAll('.dbb-size-item')];
    const btns  = items.map(i => i.querySelector('.dbb-size-btn')).filter(Boolean);
    items.forEach((item, idx) => {
      const btn = btns[idx];
      if (!btn) return;
      btn.addEventListener('click', () => this._selectSizeItem(item));
      btn.addEventListener('keydown', (e) => {
        let next = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); next = (idx + 1) % items.length; }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); next = (idx - 1 + items.length) % items.length; }
        if (next >= 0) { btns[next].focus(); this._selectSizeItem(items[next]); }
      });
    });
  }

  _selectSizeItem(item) {
    this.querySelectorAll('.dbb-size-item').forEach(i => {
      i.classList.remove('is-selected');
      const b = i.querySelector('.dbb-size-btn');
      if (b) { b.setAttribute('aria-checked', 'false'); b.tabIndex = -1; }
    });
    item.classList.add('is-selected');
    const selBtn = item.querySelector('.dbb-size-btn');
    if (selBtn) { selBtn.setAttribute('aria-checked', 'true'); selBtn.tabIndex = 0; }
    this._selectedPackSize = parseInt(item.dataset.packSize, 10);

    const label = this.querySelector('[data-size-label]');
    if (label) label.textContent = item.dataset.label || `${this._selectedPackSize}-Pack`;

    if (this._selectedVariantId === null) {
      this._autoDistributeMix();
    } else {
      this._updateMixTotal();
    }
    this._updatePrices();
    this._updateAtcState();
  }

  // ── Subscription ──────────────────────────────────────────────────────────

  _bindPurchase() {
    const cards = [...this.querySelectorAll('.dbb-purchase-card[data-purchase-type]')];
    cards.forEach((card, idx) => {
      card.addEventListener('click', () => this._selectPurchaseCard(card));
      card.addEventListener('keydown', (e) => {
        if (e.target.tagName === 'SELECT') return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._selectPurchaseCard(card); }
        let next = -1;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); next = (idx + 1) % cards.length; }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); next = (idx - 1 + cards.length) % cards.length; }
        if (next >= 0) { cards[next].focus(); this._selectPurchaseCard(cards[next]); }
      });
    });

    const sel = this.querySelector('.dbb-frequency-select');
    if (sel) {
      sel.addEventListener('change', () => {
        this._selectedPlanId = sel.value || null;
        this._updateAtcState();
      });
      sel.addEventListener('click', (e) => e.stopPropagation());
    }
  }

  _selectPurchaseCard(card) {
    this.querySelectorAll('.dbb-purchase-card').forEach(c => {
      c.classList.remove('is-selected');
      c.setAttribute('aria-checked', 'false');
      c.tabIndex = -1;
      const r = c.querySelector('input[type="radio"]');
      if (r) r.checked = false;
    });
    card.classList.add('is-selected');
    card.setAttribute('aria-checked', 'true');
    card.tabIndex = 0;
    const radio = card.querySelector('input[type="radio"]');
    if (radio) radio.checked = true;

    this._onPurchaseChange(card.dataset.purchaseType);
  }

  _onPurchaseChange(val) {
    this._purchaseType = val;

    if (val === 'subscription') {
      const sel = this.querySelector('.dbb-frequency-select');
      this._selectedPlanId = (sel && sel.value) ? sel.value : null;
    } else {
      this._selectedPlanId = null;
    }

    this._updatePrices();
    this._updateAtcState();
  }

  // ── Price Display ──────────────────────────────────────────────────────────
  // Prices live as data-* attributes on each size item (set by Liquid from block
  // settings) so that automatic cart discounts can be reflected without relying
  // on variant.price. Called on size change AND on purchase-type toggle.

  _getMoneyFormat() {
    if (this._moneyFmt) return this._moneyFmt;

    // When a currency converter has changed the active currency, prefer its format.
    const sc = window.Shopify;
    if (sc && sc.currency && sc.currency.active) {
      const shop = this._getShopCurrency();
      if (shop && sc.currency.active !== shop) {
        const activeFmt = sc.currency.money_with_currency_format || sc.currency.money_format;
        if (activeFmt) { this._moneyFmt = activeFmt; return this._moneyFmt; }
      }
    }

    const pricesEl = document.getElementById('dbb-prices-' + this.dataset.sectionId);
    if (!pricesEl) return '{{amount}}';
    try {
      this._moneyFmt = JSON.parse(pricesEl.textContent).money_format || '{{amount}}';
      return this._moneyFmt;
    } catch (_) { return '{{amount}}'; }
  }

  _getShopCurrency() {
    if (this._shopCurrency) return this._shopCurrency;
    const el = document.getElementById('dbb-prices-' + this.dataset.sectionId);
    try {
      this._shopCurrency = JSON.parse(el.textContent).shop_currency || '';
    } catch (_) {
      this._shopCurrency = '';
    }
    return this._shopCurrency;
  }

  // Converts base-currency cents to the active display currency.
  // Handles both Shopify Markets (Shopify.currency.rate) and third-party converter apps (window.Currency).
  _convertCents(cents) {
    if (!cents || isNaN(cents)) return cents || 0;

    const sc           = window.Shopify;
    const shopCurrency = this._getShopCurrency();
    const active       = (sc && sc.currency && sc.currency.active) || shopCurrency;

    // Shopify Markets / native multi-currency
    if (sc && sc.currency && sc.currency.rate) {
      const rate = parseFloat(sc.currency.rate);
      if (!isNaN(rate) && rate > 0) return Math.round(cents * rate);
    }

    // Third-party currency converter apps (e.g., BEST Currency Converter, Currency Switcher)
    if (window.Currency && typeof window.Currency.convert === 'function' && shopCurrency && active !== shopCurrency) {
      try {
        return Math.round(window.Currency.convert(cents / 100, shopCurrency, active) * 100);
      } catch (_) {}
    }

    return cents;
  }

  // Listens for currency-change events fired by Shopify Markets and common converter apps.
  // Invalidates the format cache and re-renders all prices.
  _bindCurrencyChange() {
    const handler = () => { this._moneyFmt = null; this._updatePrices(); };
    ['currency:updated', 'theme:currency:changed', 'shopify:currency:updated'].forEach(ev => {
      document.addEventListener(ev, handler);
    });
  }

  _updatePrices() {
    const fmt   = this._getMoneyFormat();
    const isSub = this._purchaseType === 'subscription';

    // Update purchase-card totals from the selected size
    const selectedItem = this.querySelector('.dbb-size-item.is-selected');
    if (selectedItem) {
      const onetimeCents = parseInt(selectedItem.dataset.onetimePrice, 10) || 0;
      const subCents     = parseInt(selectedItem.dataset.subPrice,      10) || 0;
      const onetimeEl = this.querySelector('[data-price-display="onetime"]');
      const subEl     = this.querySelector('[data-price-display="sub"]');
      if (onetimeEl) onetimeEl.textContent = onetimeCents ? this._formatMoney(onetimeCents, fmt) : '';
      if (subEl)     subEl.textContent     = subCents     ? this._formatMoney(subCents,     fmt) : '';
    }

    // Update every size card button to reflect the active purchase type
    this.querySelectorAll('.dbb-size-item').forEach(item => {
      const onetimeCents   = parseInt(item.dataset.onetimePrice,   10) || 0;
      const onetimeCompare = parseInt(item.dataset.onetimeCompare, 10) || 0;
      const onetimePerBag  = item.dataset.onetimePerBag || '';
      const subCents       = parseInt(item.dataset.subPrice,       10) || 0;
      const subCompare     = parseInt(item.dataset.subCompare,     10) || 0;
      const subPerBag      = item.dataset.subPerBag || '';

      const activeCents   = isSub ? subCents    : onetimeCents;
      const activeCompare = isSub ? subCompare  : onetimeCompare;
      const activePerBag  = isSub ? subPerBag   : onetimePerBag;

      const compareEl = item.querySelector('.dbb-size-btn__compare');
      const priceEl   = item.querySelector('.dbb-size-btn__price');
      const perBagEl  = item.querySelector('.dbb-size-btn__per-bag');

      if (compareEl) {
        if (activeCompare > 0) {
          compareEl.textContent = this._formatMoney(activeCompare, fmt);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      if (priceEl) {
        priceEl.textContent = activeCents ? this._formatMoney(activeCents, fmt) : '';
        priceEl.hidden = !activeCents;
      }
      if (perBagEl) {
        perBagEl.textContent = activePerBag;
        perBagEl.hidden = !activePerBag;
      }
    });
  }

  // Matches Shopify's standard money formatting using the shop's money_format string.
  _formatMoney(cents, format) {
    // Normalise input — guard against NaN, null, undefined, or string cents
    if (typeof cents === 'string') cents = parseInt(cents.replace(/[^0-9-]/g, ''), 10);
    if (!cents || isNaN(cents)) return '';

    const amount = this._convertCents(cents);
    const fmt    = format || this._getMoneyFormat();

    // Delegate to Shopify's built-in formatter when available — handles any future
    // format tokens and respects the active currency format set by converter apps.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(amount, fmt); } catch (_) {}
    }

    // Custom fallback — supports every token Shopify documents.
    function withDelimiters(n, precision, thousands, decimal) {
      precision = precision !== undefined ? precision : 2;
      thousands = thousands || ',';
      decimal   = decimal   || '.';
      const fixed = (n / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return precision > 0 ? parts[0] + decimal + parts[1] : parts[0];
    }

    const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(amount);
    let value;
    switch (match[1]) {
      case 'amount':                                   value = withDelimiters(amount, 2);           break;
      case 'amount_no_decimals':                       value = withDelimiters(amount, 0);           break;
      case 'amount_with_comma_separator':              value = withDelimiters(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':  value = withDelimiters(amount, 0, '.', ','); break;
      case 'amount_no_decimals_with_space_separator':  value = withDelimiters(amount, 0, ' ');      break;
      case 'amount_with_apostrophe_separator':         value = withDelimiters(amount, 2, "'");      break;
      default: value = withDelimiters(amount, 2);
    }
    return fmt.replace(match[0], value);
  }

  // ── Build Your Mix ────────────────────────────────────────────────────────

  _bindMix() {
    const rows = [...this.querySelectorAll('[data-mix-variant]')];
    rows.forEach((row, idx) => {
      const id = row.dataset.mixVariant;
      this._mixQtys[id] = 0;

      row.querySelector('[data-qty-dec]').addEventListener('click', () => {
        if (rows.length <= 1 || (this._mixQtys[id] || 0) < this._basePackQty) return;
        const nextIdx = (idx + 1) % rows.length;
        const nextId  = rows[nextIdx].dataset.mixVariant;
        this._mixQtys[id]     = (this._mixQtys[id] || 0) - this._basePackQty;
        this._mixQtys[nextId] = (this._mixQtys[nextId] || 0) + this._basePackQty;
        this._renderMixRow();
      });

      row.querySelector('[data-qty-inc]').addEventListener('click', () => {
        const nextIdx = (idx + 1) % rows.length;
        const nextId  = rows[nextIdx].dataset.mixVariant;
        if ((this._mixQtys[nextId] || 0) < this._basePackQty) return;
        this._mixQtys[id]     = (this._mixQtys[id] || 0) + this._basePackQty;
        this._mixQtys[nextId] = (this._mixQtys[nextId] || 0) - this._basePackQty;
        this._renderMixRow();
      });
    });
  }

  // Distribute the selected pack size across mix rows as evenly as possible,
  // in increments of basePackQty. Any remainder bags go to the first row(s).
  // e.g. 6-Pack ÷ 2 flavors ÷ bpq=2 → perRow=2, remainder=2 → [4, 2]
  _autoDistributeMix() {
    const rows   = [...this.querySelectorAll('[data-mix-variant]')];
    const n      = rows.length;
    if (n === 0 || !this._selectedPackSize) return;

    const pack   = this._selectedPackSize;
    const bpq    = this._basePackQty;
    const base   = Math.floor(Math.floor(pack / n) / bpq) * bpq;
    let   rem    = pack - base * n;

    rows.forEach(row => {
      const id = row.dataset.mixVariant;
      let qty  = base;
      if (rem >= bpq) { qty += bpq; rem -= bpq; }
      this._mixQtys[id] = qty;
    });

    // Flush any sub-bpq remainder (misconfigured pack/bpq) onto the first row
    // so total always equals packSize and _isMixValid() can return true.
    if (rem > 0 && rows.length > 0) {
      const firstId = rows[0].dataset.mixVariant;
      this._mixQtys[firstId] = (this._mixQtys[firstId] || 0) + rem;
    }

    this._renderMixRow();
  }

  _resetMix() {
    Object.keys(this._mixQtys).forEach(id => { this._mixQtys[id] = 0; });
    this._renderMixRow();
  }

  _renderMixRow() {
    this.querySelectorAll('[data-mix-variant]').forEach(row => {
      const valEl = row.querySelector('.dbb-qty-val');
      if (valEl) valEl.textContent = this._mixQtys[row.dataset.mixVariant] || 0;
    });
    this._updateMixTotal();
    this._updateMixButtonStates();
    this._updateAtcState();
  }

  _updateMixButtonStates() {
    const rows = [...this.querySelectorAll('[data-mix-variant]')];
    const bpq  = this._basePackQty;
    rows.forEach((row, idx) => {
      const id      = row.dataset.mixVariant;
      const qty     = this._mixQtys[id] || 0;
      const nextIdx = (idx + 1) % rows.length;
      const nextId  = rows[nextIdx].dataset.mixVariant;
      const nextQty = this._mixQtys[nextId] || 0;
      const decBtn  = row.querySelector('[data-qty-dec]');
      const incBtn  = row.querySelector('[data-qty-inc]');
      if (decBtn) decBtn.disabled = rows.length <= 1 || qty < bpq;
      if (incBtn) incBtn.disabled = rows.length <= 1 || nextQty < bpq;
    });
  }

  _mixTotal() {
    return Object.values(this._mixQtys).reduce((sum, q) => sum + q, 0);
  }

  _mixTarget() {
    // Show target as pack size units so the counter reads "3/6" for a 6-Pack
    return this._selectedPackSize || 0;
  }

  _isMixValid() {
    const target = this._mixTarget();
    return target > 0 && this._mixTotal() === target;
  }

  _updateMixTotal() {
    const target  = this._mixTarget();
    const current = this._mixTotal();
    const countEl = this.querySelector('.dbb-mix-count');
    const checkEl = this.querySelector('.dbb-mix-check');
    const hintEl  = this.querySelector('.dbb-mix-hint');
    if (!countEl) return;

    countEl.textContent = `${current} / ${target}`;

    const isValid = target > 0 && current === target;
    const isOver  = current > target;

    countEl.classList.toggle('is-valid', isValid && !isOver);
    countEl.classList.toggle('is-over',  isOver);
    if (checkEl) checkEl.classList.toggle('is-visible', isValid && !isOver);

    if (hintEl) {
      if (isValid && !isOver)  hintEl.textContent = 'Looks good — ready to add to cart.';
      else if (isOver)         hintEl.textContent = `Remove ${current - target} to complete your pack.`;
      else if (target > 0)     hintEl.textContent = `Add ${target - current} more to complete your pack.`;
      else                     hintEl.textContent = '';
    }

  }

  // ── ATC State ─────────────────────────────────────────────────────────────
  // Single source of truth: called after every state change.
  // Rule: disabled unless ALL of the following are true:
  //   1. A valid selection exists (variant ID or complete variety mix)
  //   2. If subscription: a plan is selected

  _updateAtcState() {
    const btn = this.querySelector('.dbb-atc-btn');
    if (!btn) return;

    let disabled = false;

    // Rule 1: flavor / variety
    const isVariety = this._selectedVariantId === null;
    const isUnset   = this._selectedVariantId === 'UNSET';

    if (isUnset) {
      disabled = true;
    } else if (isVariety) {
      if (!this._isMixValid()) disabled = true;
    }
    // else: single variant selected — flavor rule passes

    // Rule 2: subscription requires a plan
    if (this._purchaseType === 'subscription' && !this._selectedPlanId) {
      disabled = true;
    }

    btn.disabled = disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  // ── Cart Quantity ─────────────────────────────────────────────────────────

  _getCartQty() {
    if (!this._selectedPackSize || !this._basePackQty) return 1;
    return Math.ceil(this._selectedPackSize / this._basePackQty);
  }

  // Adds Shopify Sections API fields to the cart-add payload so the response
  // includes rendered section HTML for the cart count bubble and any cart UI components.
  _enrichWithSections(payload) {
    const ids = new Set();
    // Always request cart-icon-bubble so the header count updates on every add.
    if (document.getElementById('cart-icon-bubble')) ids.add('cart-icon-bubble');
    // Also collect whatever sections cart-notification / cart-drawer need.
    ['cart-notification', 'cart-drawer'].forEach(sel => {
      const el = document.querySelector(sel);
      if (el && typeof el.getSectionsToRender === 'function') {
        el.getSectionsToRender().forEach(s => { if (s.id) ids.add(s.id); });
      }
    });
    if (ids.size) {
      payload.sections     = [...ids];
      payload.sections_url = window.location.pathname;
    }
  }

  // Replaces the cart icon bubble's innerHTML with fresh section HTML from Shopify.
  _updateCartBubble(sectionHtml) {
    if (!sectionHtml) return;
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const doc   = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const inner = doc.querySelector('.shopify-section');
    if (inner) bubble.innerHTML = inner.innerHTML;
  }

  _cartAddUrl() {
    if (typeof routes !== 'undefined' && routes.cart_add_url) {
      const url = routes.cart_add_url;
      // Shopify themes expose /cart/add (no .js); the .js endpoint is the
      // canonical AJAX endpoint that always returns JSON and never redirects.
      return url.endsWith('.js') ? url : url + '.js';
    }
    return '/cart/add.js';
  }

  // ── Add to Cart ───────────────────────────────────────────────────────────

  _bindAtc() {
    const btn = this.querySelector('.dbb-atc-btn');
    if (btn) btn.addEventListener('click', () => this._handleAtc());
  }

  async _handleAtc() {
    const btn     = this.querySelector('.dbb-atc-btn');
    const errorEl = this.querySelector('.dbb-error');
    if (!btn || btn.disabled) return;

    btn.classList.add('is-loading');
    btn.disabled = true;
    if (errorEl) errorEl.classList.remove('is-visible');

    try {
      const isVariety = this._selectedVariantId === null;
      const response  = isVariety ? await this._addVarietyPack() : await this._addSingleVariant();
      this._onSuccess(response);
    } catch (err) {
      console.error('[DD-Buy-Box]', err);
      if (errorEl) {
        errorEl.textContent = err.message || 'Something went wrong. Please try again.';
        errorEl.classList.add('is-visible');
      }
    } finally {
      btn.classList.remove('is-loading');
      this._updateAtcState();
    }
  }

  async _addSingleVariant() {
    const variantId = this._selectedVariantId;
    if (!variantId || variantId === 'UNSET') throw new Error('Please select a flavor.');

    const payload = {
      id:       parseInt(variantId, 10),
      quantity: this._getCartQty(),
    };
    if (this._purchaseType === 'subscription' && this._selectedPlanId) {
      payload.selling_plan = parseInt(this._selectedPlanId, 10);
    }

    return this._postToCart({ items: [payload] });
  }

  async _addVarietyPack() {
    if (!this._isMixValid()) throw new Error('Please complete your mix before adding to cart.');

    const baseQty = this._basePackQty;
    const items = Object.entries(this._mixQtys)
      .filter(([, qty]) => qty > 0)
      .map(([id, qty]) => {
        const item = {
          id:       parseInt(id, 10),
          quantity: Math.ceil(qty / baseQty),
        };
        if (this._purchaseType === 'subscription' && this._selectedPlanId) {
          item.selling_plan = parseInt(this._selectedPlanId, 10);
        }
        return item;
      });

    if (!items.length) throw new Error('Please build your mix before adding to cart.');
    return this._postToCart({ items });
  }

  async _postToCart(payload) {
    this._enrichWithSections(payload);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    let res;
    try {
      res = await fetch(this._cartAddUrl(), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(payload),
        signal:  controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') throw new Error('Request timed out. Please try again.');
      throw new Error('Network error. Please check your connection and try again.');
    } finally {
      clearTimeout(timer);
    }

    let data;
    let rawText;
    try {
      rawText = await res.text();
      data    = JSON.parse(rawText);
    } catch (_) {
      console.error('[DD-Buy-Box] Non-JSON response (HTTP ' + res.status + '):', (rawText || '').slice(0, 300));
      throw new Error(
        res.ok
          ? 'Unexpected server response. Please try again.'
          : `Server error (${res.status}). Please try again.`
      );
    }

    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add to cart.');
    }
    return data;
  }

  _onSuccess(data) {
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));

    const btn = this.querySelector('.dbb-atc-btn');
    if (btn) {
      btn.textContent = 'Added to Cart!';
      setTimeout(() => { btn.textContent = this._atcLabel; }, 2200);
    }

    if (data.sections) {
      // Update header cart count — primary, always runs.
      this._updateCartBubble(data.sections['cart-icon-bubble']);

      // cart-notification / cart-drawer use parsedState.id to build the selector
      // #cart-notification-product-{id}. cart-notification-product.liquid renders
      // that div with item.id which is the variant ID, so we use items[0].id here.
      if (!data.id && data.items && data.items[0]) {
        data.id = data.items[0].id;
      }

      const notification = document.querySelector('cart-notification');
      if (notification && typeof notification.renderContents === 'function') {
        notification.renderContents(data);
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      }
    }
  }
}

if (!customElements.get('dd-buy-box')) {
  customElements.define('dd-buy-box', DDBuyBox);
}

/* ============================================================
   DD Buy Box Gallery — Web Component
   Handles: slide transitions, prev/next arrows, thumbnail sync
   ============================================================ */
class DDBuyBoxGallery extends HTMLElement {
  connectedCallback() {
    this._index  = 0;
    this._slides = [...this.querySelectorAll('[data-gallery-img]')];
    this._thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this._total  = this._slides.length;

    if (this._total <= 1) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this._go(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this._go(1));

    this._thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this._goTo(i));
    });

    // Arrow key navigation when gallery is focused
    this.setAttribute('tabindex', '-1');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this._go(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this._go(1);  }
    });

    // Touch/swipe support on the viewport
    const viewport = this.querySelector('.dbb-gallery__viewport');
    if (viewport) this._bindSwipe(viewport);

    this._render();
  }

  _go(dir) {
    this._goTo((this._index + dir + this._total) % this._total);
  }

  _goTo(i) {
    this._index = i;
    this._render();
  }

  _render() {
    const i = this._index;

    this._slides.forEach((slide, idx) => {
      const active = idx === i;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this._thumbs.forEach((thumb, idx) => {
      const active = idx === i;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });

    // Scroll the active thumbnail into view without jarring the page
    const activeThumb = this._thumbs[i];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  _bindSwipe(el) {
    let startX = null;
    el.addEventListener('pointerdown',  (e) => { startX = e.clientX; }, { passive: true });
    el.addEventListener('pointercancel', () => { startX = null; },      { passive: true });
    el.addEventListener('pointerup', (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      if (Math.abs(dx) < 40) return;
      this._go(dx < 0 ? 1 : -1);
    }, { passive: true });
  }
}

if (!customElements.get('dd-buy-box-gallery')) {
  customElements.define('dd-buy-box-gallery', DDBuyBoxGallery);
}

/* ============================================================
   Delivery Date Calculator
   Reads data-min-days / data-max-days from .dbb-delivery,
   calculates business-day range (skipping Sat/Sun),
   and fills .dbb-delivery__dates with the formatted range.
   ============================================================ */
(function () {
  function addBusinessDays(startDate, days) {
    const date = new Date(startDate);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return date;
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  function initDelivery() {
    document.querySelectorAll('.dbb-delivery[data-min-days]').forEach(function (el) {
      const min = parseInt(el.dataset.minDays, 10) || 5;
      const max = parseInt(el.dataset.maxDays, 10) || 10;
      const today = new Date();
      const from = addBusinessDays(today, min);
      const to   = addBusinessDays(today, max);
      const datesEl = el.querySelector('.dbb-delivery__dates');
      if (datesEl) datesEl.textContent = formatDate(from) + ' – ' + formatDate(to);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDelivery);
  } else {
    initDelivery();
  }
})();

/* ============================================================
   Mobile Layout — reposition buy-box column between gallery
   and testimonials on narrow screens using matchMedia so the
   handler fires only at the breakpoint, not every resize frame.
   ============================================================ */
(function () {
  const MQ = window.matchMedia('(max-width: 749px)');

  function reposition(isMobile) {
    const layout   = document.querySelector('.dbb-layout');
    const mediaCol = layout && layout.querySelector('.dbb-col--media');
    const boxCol   = layout && layout.querySelector('.dbb-col--box');
    if (!layout || !mediaCol || !boxCol) return;

    if (isMobile) {
      if (boxCol.parentElement === mediaCol) return;
      const testimonials = mediaCol.querySelector('.dbb-testimonials, .dbb-testimonials-placeholder');
      testimonials ? mediaCol.insertBefore(boxCol, testimonials) : mediaCol.appendChild(boxCol);
    } else {
      if (boxCol.parentElement === layout) return;
      layout.appendChild(boxCol);
    }
  }

  function init() {
    reposition(MQ.matches);
    MQ.addEventListener('change', (e) => reposition(e.matches));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();